<script setup lang="ts">
import { computed, ref } from "vue";
import { useClipboard } from "@vueuse/core";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Copy, Check } from "lucide-vue-next";
import CodeBlock from "@/components/CodeBlock.vue";

interface Props {
    token: string;
    embedType?: "group";
}

const props = defineProps<Props>();
const emit = defineEmits<{
    (e: "back"): void;
}>();

const { copy, copied, isSupported } = useClipboard();
const activeTab = ref("html");

const htmlCode = computed(() => {
    const options =
        props.embedType === "group"
            ? `{type: 'group', config: {token: '${props.token}'}}`
            : `{config: {token: '${props.token}'}}`;

    return `<div id="zaptime-container"></div>
<${"script"} src="https://iframe.zaptime.app/zaptime.js"></${"script"}>
<${"script"}>
    Zaptime(${options})
        .render('#zaptime-container')
</${"script"}>`;
});

const vueCode = computed(() => {
    return `<${"script"} setup lang="ts">
import { ZaptimeCalendar } from "@zaptime/vue3";
import type { ZaptimeConfig } from "@zaptime/vue3";

const config: ZaptimeConfig = {
  token: "${props.token}",
};
</${"script"}>

<template>
  <ZaptimeCalendar :config="config" />
</template>

<style>
@import url("/node_modules/@zaptime/vue3/dist/style.css");
</style>`;
});

const currentCode = computed(() => {
    return activeTab.value === "html" ? htmlCode.value : vueCode.value;
});

function copyCode() {
    copy(currentCode.value);
}
</script>

<template>
    <div class="space-y-4">
        <button
            @click="emit('back')"
            class="gap-1 mb-2 text-sm text-iron-600 hover:text-iron-900 flex items-center"
        >
            <ArrowLeft :size="16" />
            {{ $t("event_types.share_hub.embed.back") }}
        </button>

        <div>
            <h3 class="mb-1 font-medium text-iron-900">
                {{ $t("event_types.share_hub.embed.inline.title") }}
            </h3>
            <p class="text-sm text-iron-500">
                {{ $t("event_types.share_hub.embed.inline.full_description") }}
            </p>
        </div>

        <Tabs v-model="activeTab" default-value="html">
            <TabsList v-if="embedType !== 'group'" class="mb-4">
                <TabsTrigger value="html">
                    {{ $t("event_types.share_hub.embed.inline.html_tab") }}
                </TabsTrigger>
                <TabsTrigger value="vue">
                    {{ $t("event_types.share_hub.embed.inline.vue_tab") }}
                </TabsTrigger>
            </TabsList>

            <TabsContent value="html">
                <div class="rounded-lg bg-iron-50 p-4">
                    <CodeBlock :code="htmlCode" />
                </div>
            </TabsContent>

            <TabsContent v-if="embedType !== 'group'" value="vue">
                <p class="mb-3 text-sm text-iron-500">
                    {{ $t("event_types.share_hub.embed.inline.vue_install") }}
                    <code class="rounded bg-iron-100 px-1.5 py-0.5 text-xs"
                        >pnpm add @zaptime/vue3</code
                    >
                </p>
                <div class="rounded-lg bg-iron-50 p-4">
                    <CodeBlock :code="vueCode" />
                </div>
            </TabsContent>
        </Tabs>

        <Button
            v-if="isSupported"
            variant="outline"
            @click="copyCode"
            class="gap-2"
        >
            <Check v-if="copied" :size="16" />
            <Copy v-else :size="16" />
            {{
                copied
                    ? $t("copied")
                    : $t("event_types.share_hub.embed.copy_code")
            }}
        </Button>
    </div>
</template>
